import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Share, Plus, Check } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPWA() {
  const [canInstall, setCanInstall] = useState(!!window.__deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    const onAvail = () => setCanInstall(true);
    const onDone = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onDone);
    return () => { window.removeEventListener("pwa-installable", onAvail); window.removeEventListener("pwa-installed", onDone); };
  }, []);

  if (installed) {
    return (
      <div data-testid="install-card" className="mt-4 bg-surface rounded-[16px] border border-border p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-[12px] bg-success/10 flex items-center justify-center"><Check size={20} className="text-success" /></div>
        <div><p className="font-heading font-bold text-sm text-onSurface">App installed</p><p className="text-xs text-muted-foreground">Docket is on your home screen.</p></div>
      </div>
    );
  }

  const install = async () => {
    const dp = window.__deferredPrompt;
    if (dp) {
      dp.prompt();
      const { outcome } = await dp.userChoice;
      window.__deferredPrompt = null;
      if (outcome === "accepted") { setInstalled(true); toast.success("Installing Docket…"); }
      setCanInstall(false);
    } else if (isIOS()) {
      setShowIos(true);
    } else {
      toast.info("Open your browser menu and choose ‘Install app’ / ‘Add to Home screen’.");
    }
  };

  return (
    <div data-testid="install-card" className="mt-4 bg-brandTertiary/60 rounded-[16px] border border-brandPrimary/20 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-[12px] bg-brandPrimary flex items-center justify-center"><Download size={20} className="text-onBrand" /></div>
        <div className="flex-1"><p className="font-heading font-bold text-sm text-onSurface">Add Docket to your home screen</p><p className="text-xs text-muted-foreground">Launches full-screen like a normal app.</p></div>
      </div>
      <button data-testid="install-btn" onClick={install} className="w-full mt-3 bg-brandPrimary text-onBrand rounded-full h-11 font-heading font-bold text-sm active:scale-[0.98] transition-transform">
        {canInstall ? "Install app" : "How to install"}
      </button>
      {showIos && (
        <div className="mt-3 text-xs text-onSurface bg-surface rounded-[12px] p-3 space-y-1.5">
          <p className="font-semibold">On iPhone/iPad (Safari):</p>
          <p className="flex items-center gap-1.5">1. Tap the <Share size={14} className="inline text-brandPrimary" /> Share button</p>
          <p className="flex items-center gap-1.5">2. Choose <Plus size={14} className="inline text-brandPrimary" /> ‘Add to Home Screen’</p>
        </div>
      )}
    </div>
  );
}
